import React, { Ref } from 'react';
import mergeRefs from 'react-merge-refs';

import { SporsmalHeader } from '../../components/sporsmal-header/sporsmal-header';
import { FocusableHtmlElement, useFocusRef } from '../hooks';
import { KontaktinfoInput } from '../kontaktinfo/kontaktinfo-field';
import { getFeilmeldingFrom, Kontaktinfofelt } from '../kontaktinfo/kontaktinfofelt';
import { updateSkadelidtInfoSvar } from '../reducer/sporsmalsflyt-actions';
import { useSporsmalsflytDispatch } from '../reducer/sporsmalsflytDispatchProvider';
import { SporsmalAnimation } from '../sporsmalSide/sporsmalside-animation';
import { MAKSLENGDER } from '../validation/validate';
import { SkadelidtInfoBankIdOgMobil } from './skadelidt-bank-id-og-mobil-felt';
import { CurrentSkadelidtInfoSporsmal, isValid } from './skadelidtInfo-types';

import './skadelidt.scss';

const firstFieldWithError = (currentSporsmal: CurrentSkadelidtInfoSporsmal): Kontaktinfofelt | undefined => {
    const fieldOrder = [
        Kontaktinfofelt.Navn,
        Kontaktinfofelt.Mobilnummer,
        Kontaktinfofelt.Epost,
        Kontaktinfofelt.Fodselsnummer,
        Kontaktinfofelt.Organisasjonsnummer,
        Kontaktinfofelt.Bankkontonummer,
    ];
    return fieldOrder.find((field) => !isValid(currentSporsmal, field));
};

const isFirstFieldWithError = (field: Kontaktinfofelt, currentSporsmal: CurrentSkadelidtInfoSporsmal): boolean =>
    field === firstFieldWithError(currentSporsmal);

interface Props {
    currentSporsmal: CurrentSkadelidtInfoSporsmal;
    showFeilmeldinger: boolean;
    focusOnMount: boolean;
    errorRef?: Ref<FocusableHtmlElement>;
}

export const SkadelidtInfo = ({ currentSporsmal, showFeilmeldinger, errorRef, focusOnMount = true }: Props) => {
    const dispatch = useSporsmalsflytDispatch();

    const onChange = (value: string, felt: Kontaktinfofelt) => {
        dispatch(updateSkadelidtInfoSvar(felt, value));
    };

    const fornavnRef = useFocusRef<HTMLInputElement>(focusOnMount);
    const { fulltNavn, fodselsnummer, fastlegeKontor, hasBankIdOgMobilIngress, mobilnummer } =
        currentSporsmal.sporsmal.skadelidtInfoSporsmalFieldLabels;

    return (
        <div className="skadelidt container__standard-width">
            <SporsmalHeader tittel={currentSporsmal.sporsmal.tittel}>
                <div>
                    <KontaktinfoInput
                        name="navn"
                        value={currentSporsmal.fulltNavn}
                        label={fulltNavn}
                        autocomplete="name"
                        errorText={getFeilmeldingFrom(Kontaktinfofelt.Navn)}
                        isValid={!showFeilmeldinger || isValid(currentSporsmal, Kontaktinfofelt.Navn)}
                        onChange={(value) => {
                            onChange(value, Kontaktinfofelt.Navn);
                        }}
                        itemRef={mergeRefs([
                            fornavnRef,
                            isFirstFieldWithError(Kontaktinfofelt.Navn, currentSporsmal) ? errorRef : undefined,
                        ])}
                    />
                    <KontaktinfoInput
                        name="fnr"
                        value={currentSporsmal.fodselsnummer}
                        label={fodselsnummer}
                        numericKeyboard
                        errorText={getFeilmeldingFrom(Kontaktinfofelt.Fodselsnummer)}
                        isValid={!showFeilmeldinger || isValid(currentSporsmal, Kontaktinfofelt.Fodselsnummer)}
                        onChange={(value) => {
                            onChange(value, Kontaktinfofelt.Fodselsnummer);
                        }}
                        itemRef={
                            isFirstFieldWithError(Kontaktinfofelt.Fodselsnummer, currentSporsmal) ? errorRef : undefined
                        }
                    />
                    <KontaktinfoInput
                        name="fastlege"
                        value={currentSporsmal.fastlegeKontor}
                        label={fastlegeKontor}
                        errorText={`Fastlege må ha en verdi og kan ikke være mer enn ${MAKSLENGDER.fornavn} tegn.`}
                        isValid={!showFeilmeldinger || isValid(currentSporsmal, Kontaktinfofelt.Adresse)}
                        onChange={(value) => {
                            onChange(value, Kontaktinfofelt.Adresse);
                        }}
                        itemRef={isFirstFieldWithError(Kontaktinfofelt.Adresse, currentSporsmal) ? errorRef : undefined}
                        small
                    />
                    <div className="sgw-attention-message type-info">
                        <div>
                            <p className="message-text">{currentSporsmal.sporsmal.infoTekstPrimary}</p>
                        </div>
                    </div>
                    <SkadelidtInfoBankIdOgMobil
                        currentSporsmal={currentSporsmal}
                        showFeilmeldinger={showFeilmeldinger}
                        focusOnMount={focusOnMount}
                    />
                    <SporsmalAnimation animate show={currentSporsmal.hasBankIdOgMobil}>
                        <KontaktinfoInput
                            name="mobilnummer"
                            value={currentSporsmal.mobilnummer}
                            label={mobilnummer}
                            onChange={(value) => {
                                onChange(value, Kontaktinfofelt.Mobilnummer);
                            }}
                            errorText={getFeilmeldingFrom(Kontaktinfofelt.Mobilnummer)}
                            isValid={!showFeilmeldinger || isValid(currentSporsmal, Kontaktinfofelt.Mobilnummer)}
                            subLabel={hasBankIdOgMobilIngress}
                        />
                    </SporsmalAnimation>
                    <SporsmalAnimation
                        animate
                        show={currentSporsmal.hasBankIdOgMobil !== null && !currentSporsmal.hasBankIdOgMobil}
                    >
                        <div className="sgw-attention-message type-default">
                            <div>
                                <p className="message-text">{currentSporsmal.sporsmal.infoTextSecondary}</p>
                            </div>
                        </div>
                    </SporsmalAnimation>
                </div>
            </SporsmalHeader>
        </div>
    );
};
